import { defineStore } from 'pinia';
import type { RouteParamsRaw } from 'vue-router';
import { useRoute, useRouter } from 'vue-router';
import { computed, ref } from 'vue';
import { watchOnce } from '@vueuse/core';

import type { Landing } from '@libs/trip-planner-interfaces';

import { OldTripPlannerView, TripPlannerPage } from '../models/navigation';

export const useRouterStore = defineStore('RouterStore', () => {
  const router = useRouter();
  const route = useRoute();

  // State
  const isNavigating = ref(false);
  const previousTripPlannerListView = ref<TripPlannerPage>();
  const previousTripPlannerDetailsView = ref<TripPlannerPage>();
  const previousTripPlannerOverviewView = ref<TripPlannerPage>();
  const landingRoute = ref<TripPlannerPage>();
  const isInitialLoad = ref<boolean>(true);
  const landing = ref<Landing | undefined>(undefined);
  const previousTripPlannerPage = ref<TripPlannerPage | undefined>(undefined);

  // Actions
  function setLanding(param: Landing): void {
    landing.value = param;
  }

  // Getters - computed
  const queryParams = computed(() => route.query);

  // Getters - functions
  function getCurrentTripPlannerView(): TripPlannerPage | undefined {
    return Object.values(TripPlannerPage).find(
      (tripPlannerPage) => tripPlannerPage === route.name
    );
  }

  function getPreviousTripPlannerView(
    origin: OldTripPlannerView
  ): TripPlannerPage | undefined {
    if (origin === OldTripPlannerView.ProductList) {
      return previousTripPlannerListView.value;
    }

    if (origin === OldTripPlannerView.ProductDetails) {
      return previousTripPlannerDetailsView.value;
    }

    if (origin === OldTripPlannerView.Overview) {
      return previousTripPlannerOverviewView.value;
    }
  }

  function getHotelDetailsUrl(
    ingredientIndex: number,
    hotelCode: string
  ): string {
    const queryParams = window.location.search;
    return `/travel/plan/hotel-details/${ingredientIndex}/${hotelCode}${queryParams}`;
  }

  // Actions
  async function afterNavigation(): Promise<void> {
    return new Promise((resolve) => {
      if (!isNavigating.value) {
        resolve();
      } else {
        watchOnce(isNavigating, async () => resolve());
      }
    });
  }

  async function navigate({
    name,
    params,
  }: {
    name: TripPlannerPage;
    params?: RouteParamsRaw;
  }): Promise<void> {
    await afterNavigation();

    await router.push({
      name,
      params,
      query: route.query,
    });
  }

  async function navigateToCurrentTripPlanner(
    destination: OldTripPlannerView | undefined
  ): Promise<void> {
    if (destination === OldTripPlannerView.ProductList) {
      setPreviousTripPlannerListView();
    }

    if (destination === OldTripPlannerView.ProductDetails) {
      setPreviousTripPlannerDetailsView();
    }

    if (destination === OldTripPlannerView.Overview) {
      setPreviousTripPlannerOverviewView();
    }

    await navigate({ name: TripPlannerPage.CurrentTripPlanner });
  }

  async function navigateToTripOverview(): Promise<void> {
    setPreviousTripPlannerListView();
    setPreviousTripPlannerDetailsView();
    setPreviousTripPlannerOverviewView();

    await navigate({ name: TripPlannerPage.TripOverview });
  }

  async function navigateToFlightsList(): Promise<void> {
    await navigate({ name: TripPlannerPage.FlightList });
  }

  async function navigateToFerryList(): Promise<void> {
    await navigate({ name: TripPlannerPage.FerryList });
  }

  async function navigateToHotelSelection(recipeIndex: number): Promise<void> {
    // NOTE: recipeState cannot be injected in this store file to find index directly here,
    // due to circular dependency:
    // recipeState -> editingIngredientStore -> routerStore
    await navigate({
      name: TripPlannerPage.HotelSelection,
      params: { recipeIndex },
    });
  }

  async function navigateToActivitySelection(
    recipeIndex: number
  ): Promise<void> {
    // NOTE: recipeState cannot be injected in this store file to find index directly here,
    // due to circular dependency:
    // recipeState -> editingIngredientStore -> routerStore
    await navigate({
      name: TripPlannerPage.ActivitySelection,
      params: { recipeIndex },
    });
  }

  async function navigateToHotelDetails(
    recipeIndex: number,
    hotelCode: string
  ): Promise<void> {
    await navigate({
      name: TripPlannerPage.HotelDetails,
      params: { recipeIndex, hotelCode },
    });
  }

  function setPreviousTripPlannerListView(): void {
    previousTripPlannerListView.value = getCurrentTripPlannerView();
  }

  function setPreviousTripPlannerDetailsView(): void {
    previousTripPlannerDetailsView.value = getCurrentTripPlannerView();
  }

  function setPreviousTripPlannerOverviewView(): void {
    previousTripPlannerOverviewView.value = getCurrentTripPlannerView();
  }

  function setLandingRoute(): void {
    landingRoute.value = getCurrentTripPlannerView();
  }

  return {
    // State
    isNavigating,
    landingRoute,
    isInitialLoad,
    landing,
    previousTripPlannerPage,

    // Getters - computed
    queryParams,

    // Getters - functions
    getPreviousTripPlannerView,
    getCurrentTripPlannerView,
    getHotelDetailsUrl,

    // Actions
    navigateToCurrentTripPlanner,
    navigateToTripOverview,
    navigateToFlightsList,
    navigateToFerryList,
    navigateToHotelSelection,
    navigateToActivitySelection,
    navigateToHotelDetails,
    setLandingRoute,

    setLanding,
  };
});
